import React, {forwardRef, useCallback} from 'react';

import {supportsPopout} from '../../../../../libs/shared/helpers/src/environment/platformHelpers';
import {useEventHandler} from '../../../../../libs/shared/helpers/src/react/hooks/useEventHandler';
import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ScrollDirectionsEnum} from '../../../../../srcCommon/scroll/scrollerTypes';
import {useAppDispatch} from '../../../../helpers/reduxHelpers';
import {ConversationBaseModel} from '../../../../models/api/conversationModel';
import {ReselectDirectionsEnum} from '../../../../models/api/preferencesModel';
import {PopoutLocationsEnum} from '../../../../services/tracking';
import {
  CellsPanelConversationsPageState,
  stateIsCellsPanelPageConversationStateWithSearchResults,
} from '../../../../store/panels/cells/cellsPanelState';
import {InboxesConversationsParameters} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {findParametersViewId} from '../../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {CellsPanelId} from '../../../../store/panels/panelStateBase';
import {popoutConversationViewerAction} from '../../../../store/panels/viewer/viewerPanelActions';
import {ConversationCellDoubleClickHandler} from '../conversationCell';
import {CellsConversationsConversationsContainer} from './cellsConversations/cellsConversationsConversationsContainer';
import {useSelectedConversationCellDragItem} from './cellsConversations/cellsConversationsHooks';
import {CellsConversationsSearchResultsContainer} from './cellsConversations/cellsConversationsSearchResultsContainer';
import {CellsItemsChildrenRenderer, CellsItemsRequestMoreAsyncHandler} from './cellsItemsBase';

/*
 * Props.
 */

type CellsConversationsParametersPathBuilder = (parameters: InboxesConversationsParameters) => string;
type CellsConversationsChildrenRenderer = CellsItemsChildrenRenderer;

interface CellsConversationsProps extends CellsConversationsCommonProps {
  knownSelectedConversations: ReadonlyArray<ConversationBaseModel>;

  onRequestMoreAsyncInner: (
    direction: ScrollDirectionsEnum,
    isUsingSearchRawEndpoint: boolean,
  ) => Promise<void>;
}

export interface CellsConversationsChildrenProps extends CellsConversationsCommonProps {
  onDoubleClick: ConversationCellDoubleClickHandler;
  onRequestMoreAsync: CellsItemsRequestMoreAsyncHandler;
}

interface CellsConversationsCommonProps {
  panelId: CellsPanelId;
  hasNextPage: boolean;
  page: CellsPanelConversationsPageState;
  reselectDirection: ReselectDirectionsEnum;
  hasCtaVariant: boolean;

  buildParametersPath?: CellsConversationsParametersPathBuilder;
  onParametersChange: HandlerOf<InboxesConversationsParameters>;
  onRetryClick: Handler;
  children: CellsConversationsChildrenRenderer;
}

/*
 * Component.
 */

export const CellsConversations = forwardRef<HTMLDivElement, CellsConversationsProps>((props, ref) => {
  const {
    panelId,
    page,
    hasNextPage,
    reselectDirection,
    hasCtaVariant,
    buildParametersPath,
    onParametersChange,
    onRetryClick,
    onRequestMoreAsyncInner,
    children,
  } = props;

  const isUsingSearchRawEndpoint = stateIsCellsPanelPageConversationStateWithSearchResults(page);
  const onRequestMoreAsync = useCallback(
    (direction: ScrollDirectionsEnum) => onRequestMoreAsyncInner(direction, isUsingSearchRawEndpoint),
    [onRequestMoreAsyncInner, isUsingSearchRawEndpoint],
  );

  const sourceCustomViewId = findParametersViewId(page.parameters);
  const selectedDragItem = useSelectedConversationCellDragItem(
    page.cellsContext,
    props.knownSelectedConversations,
    sourceCustomViewId,
  );

  const dispatch = useAppDispatch();
  const onDoubleClick: ConversationCellDoubleClickHandler = useEventHandler((conversationId) => {
    if (!supportsPopout()) {
      return;
    }

    dispatch(
      popoutConversationViewerAction(
        conversationId,
        undefined,
        page.parameters,
        PopoutLocationsEnum.DOUBLE_CLICK_P2,
      ),
    );
  });

  if (isUsingSearchRawEndpoint) {
    return (
      <CellsConversationsSearchResultsContainer
        panelId={panelId}
        page={page}
        hasNextPage={hasNextPage}
        reselectDirection={reselectDirection}
        selectedDragItem={selectedDragItem}
        hasCtaVariant={hasCtaVariant}
        buildParametersPath={buildParametersPath}
        onParametersChange={onParametersChange}
        onDoubleClick={onDoubleClick}
        onRetryClick={onRetryClick}
        onRequestMoreAsync={onRequestMoreAsync}
      >
        {children}
      </CellsConversationsSearchResultsContainer>
    );
  }

  return (
    <CellsConversationsConversationsContainer
      panelId={panelId}
      page={page}
      hasNextPage={hasNextPage}
      reselectDirection={reselectDirection}
      selectedDragItem={selectedDragItem}
      hasCtaVariant={hasCtaVariant}
      buildParametersPath={buildParametersPath}
      onParametersChange={onParametersChange}
      onDoubleClick={onDoubleClick}
      onRetryClick={onRetryClick}
      onRequestMoreAsync={onRequestMoreAsync}
    >
      {children}
    </CellsConversationsConversationsContainer>
  );
});
